import { useAuth0UserData } from '@gr/portal/hooks/useAuth0Custom';
import { CampaignTypeEnum } from '@gr/shared/enums';
import { Auth0Role, IAreaCodesByCampaignType, IClientView, IHttpResponse } from '@gr/shared/models';
import { FastField, Field, FieldArray, Formik } from 'formik';
import { Fragment, useEffect, useRef, useState } from 'react';
import * as yup from 'yup';
import { axiosGet } from '../../authAxios';
import useProvidersForCampaignType from '../../hooks/useProvidersForCampaignType';
import useRoles from '../../hooks/useRoles';
import { convertEnumToReadableString } from '../../providers/utility.provider';
import DetailsPanelCard from '../shared/DetailsPanel/DetailsPanelCard';
import { Dropdown, IDropdownValue } from '../shared/Form/Dropdown';
import AssignedAgentsDropdown from '../shared/Form/Dropdowns/AssignedAgentsDropdown';
import ProviderForClientDropdown from '../shared/Form/Dropdowns/ProviderForClientDropdown';
import { IRadioButtonOption, RadioButtonGroup } from '../shared/Form/RadioButton';
import { Slider } from '../shared/Form/Slider';
import { TextInput } from '../shared/Form/TextInput';
import LoadingIndicator from '../shared/LoadingIndicator';
import Roles from '../shared/Roles';
import { Transition } from '@headlessui/react';

export interface IClientForm {
  name: string;
  isActive?: IRadioButtonOption;
  externalId?: string;
  senderIdentifier?: string;
  tcrBrandId?: string;
  messageFlow?: string;
  defaultClickerGroupId?: string;
  twoWayEnabled: boolean;
  priorityLevel?: number;
  providers?: Array<{ id: string | undefined; campaignType: string | undefined; provider: string | undefined; }>;
}

// Priority tiers presented to admins map to the underlying scale (MES-2526): High = 2, Medium = 3, Low = 4.
// Lower numbers send earlier in the pipe. Medium (3) is the default for new clients.
export const DEFAULT_PRIORITY_LEVEL = 3;

const priorityOptions: IDropdownValue[] = [
  { label: 'High', value: 2 },
  { label: 'Medium', value: 3 },
  { label: 'Low', value: 4 },
];

// Clamp a stored priority to a value the dropdown can represent. If the saved value is missing
// (e.g. a data source that doesn't return the column) or out of the exposed {2,3,4} range (e.g. a
// legacy `1`), fall back to the default so the form never submits a tier the user didn't choose and
// never blocks on Yup's oneOf([2,3,4]) validation.
const toPriorityLevel = (value: number | undefined): number =>
  value !== undefined && priorityOptions.some((option) => option.value === value) ? value : DEFAULT_PRIORITY_LEVEL;

const options: IRadioButtonOption[] = [
  {
    label: 'Enabled',
    value: true,
  },
  {
    label: 'Disabled',
    value: false,
  },
];

// The client's saved provider per campaign type (keyed by CampaignTypeEnum value), captured from
// the same fetch that seeds the provider dropdowns. updateClient compares against it so only
// providers the user actually changed are swapped (MES-2587).
export type ClientProviderMapping = Partial<Record<string, string>>;

interface IClientFormProps {
  item: IClientView | undefined;
  onSubmit?: (formValues: IClientForm, originalProviders?: ClientProviderMapping) => void;
  isReadyOnly?: boolean;
}

const ClientsForm = ({ item, onSubmit, isReadyOnly }: IClientFormProps): JSX.Element => {
  const [loading, setLoading] = useState(false);
  const [{ data: providers, loading: providersLoading, error: providersErrors }, fetchProviders] =
    useProvidersForCampaignType(item?.id ?? '');
  const { user } = useAuth0UserData();
  const providerRoles = [Auth0Role.GR_ADMIN];
  const providerPermissions = useRoles(providerRoles);
  useEffect(() => {
    if (item && providerPermissions) {
      const fetchData = async () => {
        try {
          await fetchProviders();
        } catch (ex) {
          console.error(ex);
          // Swallow this, we don't care
        }
      };
      fetchData();
    }
  }, [item]);

  const allowAreaCodes = useRoles([Auth0Role.GR_ADMIN, Auth0Role.GR_STAFF_MANAGER, Auth0Role.P2P_ADMIN, Auth0Role.P2P_STAFF_MANAGER]);
  const [areaCodesExpanded, setAreaCodesExpanded] = useState(false);
  const [areaCodes, setAreaCodes] = useState<IAreaCodesByCampaignType[]>([]);
  const [areaCodesLoading, setAreaCodesLoading] = useState(false);
  const [areaCodesError, setAreaCodesError] = useState(false);
  const currentClientId = useRef<string | undefined>(undefined);

  useEffect(() => {
    setAreaCodes([]);
    setAreaCodesError(false);
    setAreaCodesExpanded(false);
    if (item?.id && allowAreaCodes) {
      getAreaCodes(item.id);
    } else {
      // No active fetch for this render — clear the ref so a late response
      // from a previously selected client can no longer pass the guard below.
      currentClientId.current = undefined;
    }
  }, [item, allowAreaCodes]);

  const getAreaCodes = async (clientId: string) => {
    currentClientId.current = clientId;
    setAreaCodesLoading(true);
    setAreaCodesError(false);
    try {
      const {
        data: { data },
      } = await axiosGet<IHttpResponse<IAreaCodesByCampaignType[]>>(`/system-numbers/${clientId}`);
      if (currentClientId.current !== clientId) return;
      setAreaCodes(data ?? []);
    } catch (err) {
      console.error(err);
      if (currentClientId.current === clientId) {
        setAreaCodesError(true);
      }
    }
    if (currentClientId.current === clientId) {
      setAreaCodesLoading(false);
    }
  };

  const allowSenderId = useRoles([Auth0Role.GR_ADMIN]);
  const allowTCRBrandId = useRoles([Auth0Role.GR_ADMIN]);

  // Original provider mapping for the dirty check (MES-2587). Empty until the provider fetch
  // resolves — in that case every touched provider counts as changed, matching today's behavior.
  const originalProviderMapping: ClientProviderMapping = (providers?.data?.types ?? []).reduce(
    (mapping, type) => ({ ...mapping, [type.name]: type.providerId }),
    {} as ClientProviderMapping
  );

  const initialFormState: IClientForm = {
    name: item?.name ?? '',
    isActive:
      item?.isActive !== undefined && item?.isActive !== null
        ? options.find((option) => option.value === item?.isActive)
        : options[0],
    providers: [],
    senderIdentifier: item?.senderIdentifier ?? '',
    tcrBrandId: item?.tcrBrandId ?? '',
    externalId: item?.externalId ?? '',
    messageFlow: item?.messageFlow ?? '',
    defaultClickerGroupId: item?.defaultClickerGroupId ?? undefined,
    twoWayEnabled: item?.twoWayEnabled ?? true,
    priorityLevel: toPriorityLevel(item?.priorityLevel),
  };

  const schema: yup.SchemaOf<IClientForm> = yup.object().shape({
    name: yup
      .string()
      .defined('Required')
      .test('all-clients', 'Name cannot be All Clients', (value) => value?.toLowerCase().trim() !== 'all clients'),
    isActive: yup
      .object()
      .shape({
        label: yup.string(),
        value: yup.boolean().required('Required'),
      })
      .nullable(),
    externalId: yup.string(),
    senderIdentifier: yup.string(),
    tcrBrandId: yup.string(),
    messageFlow: yup.string().url('Call To Action must be a valid URL'),
    providers: yup.array().of(
      yup.object().shape({
        id: yup.string(),
        campaignType: yup.string(),
        provider: yup.string(),
      })
    ),
    defaultClickerGroupId: yup.string(),
    twoWayEnabled: yup.boolean().defined(),
    priorityLevel: yup.number().oneOf([2, 3, 4]).default(DEFAULT_PRIORITY_LEVEL),
  });

  return (
    <>
      <Formik
        initialValues={initialFormState}
        validationSchema={schema}
        onSubmit={async (values) => {
          onSubmit && onSubmit(values, originalProviderMapping);
        }}
      >
        {({ values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue, setFieldTouched }) => (
          // Set form id so external submit button can still work
          <>
            <form id="client-form" onSubmit={handleSubmit}>
              <div className="flex flex-col space-y-4">
                <TextInput
                  id="name"
                  name="name"
                  label="Name"
                  disabled={isReadyOnly}
                  value={values.name}
                  error={touched.name ? errors.name : ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />

                <Field
                  name="isActive"
                  component={RadioButtonGroup}
                  options={options}
                  label="Status"
                  disabled={isReadyOnly}
                  error={touched.isActive && (errors.isActive as any)?.value}
                  value={values.isActive}
                  onBlur={handleBlur}
                  onChange={(isActive: IRadioButtonOption) => {
                    setFieldTouched('isActive');
                    setFieldValue('isActive', isActive);
                  }}
                />

                {/* SUPER ADMIN ONLY */}
                <Roles roles={[Auth0Role.GR_ADMIN]}>
                  <Dropdown
                    label="Priority"
                    disabled={isReadyOnly}
                    options={priorityOptions}
                    value={priorityOptions.find((option) => option.value === values.priorityLevel) ?? null}
                    error={touched.priorityLevel ? (errors.priorityLevel as string) : ''}
                    onChange={(selected) => {
                      setFieldTouched('priorityLevel');
                      setFieldValue('priorityLevel', selected.value);
                    }}
                    onBlur={() => setFieldTouched('priorityLevel')}
                    full
                  />
                </Roles>

                <Roles roles={[Auth0Role.GR_STAFF_USER]}>
                  <AssignedAgentsDropdown
                    label="Default Agent Group"
                    value={values.defaultClickerGroupId}
                    onChange={(newValue) => {
                      setFieldTouched('defaultClickerGroupId');
                      setFieldValue('defaultClickerGroupId', newValue?.value);
                    }}
                    onBlur={() => {
                      setFieldTouched('defaultClickerGroupId');
                    }}
                    defaultGroupName="None"
                    errorMessage={errors.defaultClickerGroupId as string}
                    showError={!!(touched.defaultClickerGroupId && errors.defaultClickerGroupId)}
                    includeNone
                  />
                </Roles>

                {item?.id && allowAreaCodes && (
                  <div className="overflow-hidden bg-white shadow sm:rounded-lg">
                    <div
                      className="px-4 py-3 sm:px-6 hover:cursor-pointer"
                      onClick={() => setAreaCodesExpanded(!areaCodesExpanded)}
                    >
                      <h3 className="text-lg font-medium leading-6 text-sky-600 hover:underline">Area Codes</h3>
                      <p className="max-w-2xl mt-1 text-sm text-gray-500">Geographic coverage from active sending numbers</p>
                    </div>
                    <Transition
                      show={areaCodesExpanded}
                      as={Fragment}
                      enter="ease-in-out duration-200"
                      enterFrom="opacity-0"
                      enterTo="opacity-100"
                      leave="ease-in-out duration-200"
                      leaveFrom="opacity-100"
                      leaveTo="opacity-0"
                    >
                      <div className="border-t border-gray-200 px-4 py-3 sm:px-6">
                        {areaCodesLoading ? (
                          <LoadingIndicator position="CENTER" />
                        ) : areaCodesError ? (
                          <p className="text-sm text-rose-600">Unable to load area codes. Please try again.</p>
                        ) : areaCodes.length === 0 ? (
                          <p className="text-sm text-gray-500">No area codes available</p>
                        ) : (
                          areaCodes.map((group, index) => (
                            <div
                              key={group.campaignType}
                              className={`flex flex-col ${index !== 0 ? 'border-t border-gray-200 dark:border-slate-800 py-4' : 'pb-4'}`}
                            >
                              <h4 className="mb-2 text-sm font-medium text-gray-700 dark:text-slate-400">
                                {group.campaignType === CampaignTypeEnum.GOTV
                                  ? 'GOTV'
                                  : convertEnumToReadableString(group.campaignType)}
                              </h4>
                              {group.isTollFree ? (
                                <div className="flex flex-wrap gap-2">
                                  <span className="inline-flex items-center rounded-full bg-sky-100 px-3 py-1 text-sm font-medium text-sky-800">
                                    Toll-Free
                                  </span>
                                </div>
                              ) : group.areaCodes.length === 0 ? (
                                <p className="text-sm text-gray-500">No area codes available</p>
                              ) : (
                                <div className="flex flex-wrap gap-2">
                                  {group.areaCodes.map((code) => (
                                    <span
                                      key={code}
                                      className="inline-flex items-center rounded-full bg-sky-100 px-3 py-1 text-sm font-medium text-sky-800"
                                    >
                                      {code}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    </Transition>
                  </div>
                )}

                {loading && <LoadingIndicator position="CENTER" />}

                {item?.id && !loading && providerPermissions && (
                  <Roles roles={providerRoles}>
                    <DetailsPanelCard title="Providers" subtitle="Change providers by campaign type">
                      <div className="">
                        <FieldArray
                          name="providers"
                          render={(arrayHelpers) =>
                            Object.values(CampaignTypeEnum).map((type, index) => {
                              return (
                                <div
                                  key={type}
                                  className={`flex flex-col sm:flex-row sm:items-center pl-2 ${index !== 0 ? 'border-t border-gray-200 dark:border-slate-800 py-4' : 'py-4'
                                    }`}
                                >
                                  <h4 className="text-sm font-medium text-gray-700 sm:w-1/2 dark:text-slate-400">
                                    {type === CampaignTypeEnum.GOTV ? 'GOTV' : convertEnumToReadableString(type)}
                                  </h4>
                                  <ProviderForClientDropdown
                                    clientId={item?.id ?? ''}
                                    campaignType={type}
                                    value={
                                      values?.providers?.[index]?.provider ??
                                      providers?.data.types
                                        .map((x) => {
                                          return {
                                            campaignType: x.name,
                                            provider: x.providerId,
                                          };
                                        })
                                        .find((y) => y.campaignType === type)?.provider
                                    }
                                    onChange={(newValue) => {
                                      setFieldValue(`providers.${index}.provider`, newValue?.value ?? '');
                                      setFieldValue(`providers.${index}.campaignType`, type);
                                    }}
                                    onBlur={() => {
                                      setFieldTouched(`providers.${index}.provider`);
                                    }}
                                    touched={
                                      !!(
                                        touched.providers?.[index]?.provider &&
                                        (errors.providers?.[index] as any)?.provider
                                      )
                                    }
                                    errorMessage={(errors?.providers?.[index] as any)?.provider}
                                  />
                                </div>
                              );
                            })
                          }
                        />
                      </div>
                    </DetailsPanelCard>
                  </Roles>
                )}

                {/* TODO: Disable unless super admin, don't make it required */}
                <FastField
                  component={TextInput}
                  id="senderIdentifier"
                  name="senderIdentifier"
                  label="Sender Identifier"
                  disabled={!allowSenderId || isReadyOnly}
                  value={values.senderIdentifier}
                  error={touched.senderIdentifier ? errors.senderIdentifier : ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />

                <FastField
                  component={TextInput}
                  id="tcrBrandId"
                  name="tcrBrandId"
                  label="TCR Brand Id"
                  disabled={!allowTCRBrandId || isReadyOnly}
                  value={values.tcrBrandId}
                  error={touched.tcrBrandId ? errors.tcrBrandId : ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />

                <FastField
                  component={TextInput}
                  id="externalId"
                  name="externalId"
                  label="External Id"
                  disabled={isReadyOnly}
                  value={values.externalId}
                  error={touched.externalId ? errors.externalId : ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />

                <FastField
                  component={TextInput}
                  id="messageFlow"
                  name="messageFlow"
                  label="Call To Action URL"
                  disabled={isReadyOnly}
                  value={values.messageFlow}
                  error={touched.messageFlow ? errors.messageFlow : ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />

                {/* SUPER ADMIN ONLY */}
                <Roles roles={[Auth0Role.GR_ADMIN]}>
                  <FastField
                    component={Slider}
                    id="twoWayEnabled"
                    name="twoWayEnabled"
                    label="Two Way Conversations"
                    disabled={isReadyOnly}
                    value={values.twoWayEnabled}
                    checked={values.twoWayEnabled}
                    error={touched.twoWayEnabled ? errors.twoWayEnabled : ''}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </Roles>
              </div>
            </form>
          </>
        )}
      </Formik>
    </>
  );
};

export default ClientsForm;
